import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { playChatSound } from '@/lib/chat-sound';
import { queryClient } from '@/lib/query-client';
import type { User } from '@shared/schema';

export function useChatNotifications(): void {
  const { data: currentUser } = useQuery<User>({ queryKey: ['/api/auth/user'] });
  const { data: spaces = [] } = useQuery<any[]>({ queryKey: ['/api/chat/spaces'] });
  const [location] = useLocation();

  const spaceIdSet = useRef<Set<string>>(new Set());
  useEffect(() => {
    spaceIdSet.current = new Set((spaces as any[]).map((s) => s.id as string));
  }, [spaces]);

  useEffect(() => {
    if (!currentUser || location === '/chat') return;

    const es = new EventSource('/api/sse/chat', { withCredentials: true });

    es.onmessage = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as { type: string; data: { spaceId?: string; senderId?: string } };
        const { type: eventType } = payload;
        const { spaceId: sid, senderId } = payload.data ?? {};
        if (!sid || !spaceIdSet.current.has(sid)) return;

        if (eventType === 'chat:message:edited' || eventType === 'chat:message:deleted') {
          queryClient.invalidateQueries({ queryKey: [`/api/chat/spaces/${sid}/messages`] });
          return;
        }

        if (eventType !== 'chat:message') return;
        if (senderId === currentUser.id) return;
        playChatSound();
      } catch { /* ignore */ }
    };

    return () => es.close();
  }, [currentUser?.id, location]);
}
